import React, { useEffect, useCallback } from 'react';
import moment from 'moment-timezone';
import _isNil from 'lodash/isNil';
import {
  ActiveAppointmentAvailabilitySlot,
  AppointmentAvailability,
  AppointmentType,
} from '../../../firebase/firestore/documents/appointmentAvaiability';
import { TimezoneValue } from './TimezoneSelect';

const roundToNearest30 = (date: Date): Date => {
  const m = moment(date);
  const minutes = m.minutes();
  /* eslint-disable-next-line */
  const rounded = minutes < 15 ? 0 : /* istanbul ignore next */ minutes < 45 ? 30 : 0;
  const addHour = minutes >= 45 ? /* istanbul ignore next */ 1 : 0;
  return m.clone().minutes(rounded).seconds(0).milliseconds(0).add(addHour, 'hours').toDate();
};

export type UseBookingFormProps = {
  scheduleDetail?: AppointmentAvailability;
  activeAppointmentSlot: ActiveAppointmentAvailabilitySlot | undefined;
  isCustomTimezoneCalendar: boolean;
  primaryTimezone?: TimezoneValue;
};

export const useBookingForm = ({
  scheduleDetail,
  activeAppointmentSlot,
  isCustomTimezoneCalendar,
  primaryTimezone,
}: UseBookingFormProps) => {
  const [appointmentTypes, setAppointmentTypes] = React.useState<AppointmentType[]>(
    scheduleDetail?.appointmentTypes ?? [AppointmentType.inPerson, AppointmentType.video, AppointmentType.phone],
  );
  const [startDate, setStartDate] = React.useState<Date | null>();
  const [startTime, setStartTime] = React.useState<Date | null>();
  const [endTime, setEndTime] = React.useState<Date | null>();
  const [bookingCapacity, setBookingCapacity] = React.useState<number>(1);
  const [selectedTimezone, setSelectedTimezone] = React.useState<TimezoneValue>();

  // Set timezone from user profile
  useEffect(() => {
    if (isCustomTimezoneCalendar) {
      setSelectedTimezone(primaryTimezone as TimezoneValue);
    }
  }, [isCustomTimezoneCalendar, primaryTimezone]);

  const clearAllFields = useCallback(() => {
    setAppointmentTypes([AppointmentType.inPerson, AppointmentType.video, AppointmentType.phone]);
    setStartDate(null);
    setStartTime(null);
    setEndTime(null);
  }, []);

  // Utility: Convert Absolute DB/Calendar Time -> Fake Local Time for UI
  const convertAbsoluteToFakeLocal = useCallback(
    (absoluteDate: Date | null | undefined): Date | null => {
      /* istanbul ignore next */
      if (!absoluteDate) return null;
      /* istanbul ignore next */
      if (!isCustomTimezoneCalendar || !selectedTimezone?.name) return moment(absoluteDate).toDate();

      // Read what the visual wall time is in the target timezone
      /* istanbul ignore next */
      const wallTimeStr = moment(absoluteDate).tz(selectedTimezone.name).format('YYYY-MM-DDTHH:mm:ss');

      // Parse it as a browser local date to create our "Fake Time"
      /* istanbul ignore next */
      return moment(wallTimeStr).toDate();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCustomTimezoneCalendar, selectedTimezone?.name],
  );

  // Re-apply timezone conversion when selected timezone changes
  useEffect(() => {
    if (!isCustomTimezoneCalendar || !selectedTimezone?.name) return;

    const reapplyTZ = (prev: Date | null | undefined): Date | null | undefined => {
      /* istanbul ignore next */
      if (!prev) return prev;
      const wallTimeStr = moment(prev).utcOffset(0, true).format('YYYY-MM-DDTHH:mm:ss');
      return roundToNearest30(moment.tz(wallTimeStr, selectedTimezone.name).toDate());
    };

    if (startDate) setStartDate((prev) => reapplyTZ(prev));
    /* istanbul ignore next */
    if (startTime) setStartTime((prev) => reapplyTZ(prev));
    /* istanbul ignore next */
    if (endTime) setEndTime((prev) => reapplyTZ(prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimezone?.name]);

  // Populate fields from scheduleDetail (edit mode)
  useEffect(() => {
    if (scheduleDetail) {
      setAppointmentTypes(scheduleDetail.appointmentTypes);
      setStartDate(convertAbsoluteToFakeLocal(scheduleDetail.fromDate));
      setStartTime(convertAbsoluteToFakeLocal(scheduleDetail.fromDate));
      setEndTime(convertAbsoluteToFakeLocal(scheduleDetail.toDate));
      setBookingCapacity(scheduleDetail.bookingCapacity || /* istanbul ignore next */ 1);
    }
  }, [scheduleDetail, convertAbsoluteToFakeLocal]);

  // Populate fields from a clicked calendar slot (receives actualStart/actualEnd)
  useEffect(() => {
    if (!_isNil(activeAppointmentSlot)) {
      setStartDate(convertAbsoluteToFakeLocal(activeAppointmentSlot.fromDate));
      setStartTime(convertAbsoluteToFakeLocal(activeAppointmentSlot.fromDate));
      setEndTime(convertAbsoluteToFakeLocal(activeAppointmentSlot.toDate));
    }
  }, [activeAppointmentSlot, convertAbsoluteToFakeLocal]);

  const isInPersonOnly = (): boolean => appointmentTypes.length === 1 && appointmentTypes.includes(AppointmentType.inPerson);

  const getCurrentTimeInSelectedTZ = (): Date => {
    if (!isCustomTimezoneCalendar || !selectedTimezone?.name) return moment().toDate();
    const nowInTZ = moment().tz(selectedTimezone.name);
    return moment().hours(nowInTZ.hours()).minutes(nowInTZ.minutes()).seconds(0).milliseconds(0).toDate();
  };

  const addTimeToDate = (sDate: Date, eDate: Date): Date => {
    const eMoment = moment(eDate);
    return moment(sDate).set('minutes', eMoment.get('minutes')).set('hours', eMoment.get('hours')).set('seconds', 0).toDate();
  };

  // Utility: Convert Fake Local Time UI -> Absolute DB Time for Saving
  const convertFakeLocalToAbsolute = (fakeDate: Date): Date => {
    /* istanbul ignore next */
    if (!isCustomTimezoneCalendar || !selectedTimezone?.name) return fakeDate;
    const wallTimeStr = moment(fakeDate).utcOffset(0, true).format('YYYY-MM-DDTHH:mm:ss');
    return moment.tz(wallTimeStr, selectedTimezone.name).toDate();
  };

  const buildBookingDates = (): { formattedStartDate: Date; formattedEndDate: Date } | null => {
    if (!startDate || !startTime || !endTime) return null;
    return {
      formattedStartDate: convertFakeLocalToAbsolute(addTimeToDate(startDate, startTime)),
      formattedEndDate: convertFakeLocalToAbsolute(addTimeToDate(startDate, endTime)),
    };
  };

  const setStartDateOfAvailability = (date: Date) => {
    setStartDate(date);
    setStartTime(null);
    setEndTime(null);
  };

  const setStartTimeOfAvailability = (date: Date) => {
    setStartTime(date);
    setEndTime(null);
  };

  return {
    appointmentTypes,
    startDate,
    startTime,
    endTime,
    bookingCapacity,
    selectedTimezone,
    isInPersonOnly,
    setAppointmentTypes,
    setEndTime,
    setBookingCapacity,
    setSelectedTimezone,
    setStartDateOfAvailability,
    setStartTimeOfAvailability,
    clearAllFields,
    getCurrentTimeInSelectedTZ,
    buildBookingDates,
  };
};
